import { useRouter } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { AccountRow } from "@/hooks/use-accounts";
import { useAccounts } from "@/hooks/use-accounts";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

const PAGE_SIZE = 50;

// Tiers are categorization, not lifecycle status, so they stay on Badge (§1i).
// Strategic keeps its amber highlight via the warning ramp tokens (the Badge
// `warning` variant was removed in the NOS Phase 2 parity pass).
const TIER_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  Support: "outline",
  Growth: "secondary",
  Standard: "default",
};

const TIER_CLASS: Record<string, string> = {
  Strategic: "border-transparent bg-warning-500/15 text-warning-700 dark:text-warning-400",
};

function ExpectationLevelCell({ level }: { level: number }) {
  const labels: Record<number, string> = {
    1: "Self-Sufficient",
    2: "Cooperative",
    3: "Standard",
    4: "Demanding",
    5: "Critical",
  };
  const label = labels[level] ?? String(level);
  return (
    <span className="text-[12px] text-muted-foreground">
      {level} — {label}
    </span>
  );
}

function AccountTableRow({ row, onClick }: { row: AccountRow; onClick: (id: string) => void }) {
  return (
    <tr
      className="cursor-pointer border-b border-border/50 transition-colors hover:bg-muted/40"
      onClick={() => onClick(row.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(row.id);
        }
      }}
      tabIndex={0}
      aria-label={`Account: ${row.name}`}
    >
      <td className="px-3 py-2.5">
        <span className="font-medium text-[13px]">{row.name}</span>
        {row.industry && (
          <span className="ml-2 text-[11px] text-muted-foreground">{row.industry}</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        <Badge
          variant={TIER_VARIANT[row.tierName] ?? "outline"}
          className={TIER_CLASS[row.tierName]}
        >
          {row.tierName}
        </Badge>
      </td>
      <td className="px-3 py-2.5">
        <ExpectationLevelCell level={row.expectationLevel} />
      </td>
    </tr>
  );
}

export function AccountsTable() {
  const router = useRouter();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError, error, refetch } = useAccounts({
    search: debouncedSearch,
    cursor,
    direction,
    limit: PAGE_SIZE,
  });

  function resetPaging() {
    setCursor(undefined);
    setDirection("next");
    setPage(1);
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    resetPaging();
  }

  function goNext() {
    if (!data?.nextCursor) return;
    setCursor(data.nextCursor);
    setDirection("next");
    setPage((p) => p + 1);
  }

  function goPrev() {
    if (!data?.prevCursor) return;
    setCursor(data.prevCursor);
    setDirection("prev");
    setPage((p) => Math.max(1, p - 1));
  }

  function handleRowClick(id: string) {
    router.navigate({ to: "/dashboard/accounts/$id", params: { id } });
  }

  const rows = data?.data ?? [];

  return (
    <div className="px-6 py-5">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[20px] font-bold leading-tight">Accounts</h1>
          <p className="text-[12px] text-muted-foreground">
            Account profiles, Engagement Standard Tier, Expectation Level, and constraint baselines.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <SearchInput
          value={search}
          onValueChange={handleSearchChange}
          placeholder="Search accounts..."
          className="max-w-sm"
        />
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load accounts."}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading &&
        !isError &&
        data !== undefined &&
        (rows.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No accounts"
            description={
              debouncedSearch
                ? `No accounts match "${debouncedSearch}".`
                : "No accounts have been set up yet."
            }
          />
        ) : (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-md border border-border bg-card">
              <table className="w-full text-[12px]">
                <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Account</th>
                    <th className="px-3 py-2 font-semibold">Tier</th>
                    <th className="px-3 py-2 font-semibold">Expectation Level</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <AccountTableRow key={row.id} row={row} onClick={handleRowClick} />
                  ))}
                </tbody>
              </table>
            </div>

            <CursorPagination
              total={data.total}
              page={page}
              limit={PAGE_SIZE}
              hasNext={data.hasNext}
              hasPrev={data.hasPrev}
              onNext={goNext}
              onPrev={goPrev}
              loading={isFetching}
              itemLabel="accounts"
            />
          </div>
        ))}
    </div>
  );
}
